import {
  AdPlatform,
  GoogleAdsIDs,
  InstanceRow,
  StyleGuideRow,
  TrainingRow
} from '@blisspointmedia/bpm-types/dist/CopyTiger';
import { IntentType } from '../../Components/designConstants';
import { AuditConfig, AuditConfigInput, AuditConfigRowWire } from './audit-config-constants';
import { auditConfigFromWire, auditConfigToWireBody } from './audit-config-utils';
import {
  awaitJSON,
  CopyTigerLambdaFetch,
  CopyTigerPyLambdaFetch,
  fetchCometSelfInvoke,
  getSafeErrorMessage,
  S3SignedUrlFetch2,
  assertPresignedOk
} from '../../utils/fetch-utils';

export interface DialogState {
  body: string;
  onConfirm?: () => void;
  title: string;
  intent: IntentType;
}

export const SORT_OPTIONS = [
  { value: 'a-to-z', label: 'A to Z' },
  { value: 'z-to-a', label: 'Z to A' },
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' }
] as const;

export type SortBy = (typeof SORT_OPTIONS)[number]['value'];

export const sortByKey = <T extends { name: string; lastModified: string }>(
  items: readonly T[],
  sortBy: SortBy
): T[] =>
  [...items].sort((a, b) => {
    if (sortBy === 'z-to-a') return b.name.localeCompare(a.name);
    if (sortBy === 'newest')
      return new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime();
    if (sortBy === 'oldest')
      return new Date(a.lastModified).getTime() - new Date(b.lastModified).getTime();
    return a.name.localeCompare(b.name);
  });

export const formatLastModified = (date: string, user: string | undefined): string =>
  `${new Date(date).toLocaleDateString()} by ${user ?? 'Unknown'}`;

export type DescriptionFields = Partial<Record<`description${1 | 2 | 3 | 4}`, string>>;

export type HeadlineFields = Partial<
  Record<`headline${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15}`, string>
>;

export type TableRow = DescriptionFields &
  HeadlineFields &
  Omit<TrainingRow, 'headlines' | 'descriptions'> & {
    id: string;
  };

export interface TrainingData {
  headlines: string[];
  descriptions: string[];
  keywords: string;
}

export type SelectedRowsMap = Record<string, TableRow>;

export type KeywordsMap = Record<
  string,
  {
    accountName: string;
    platform: AdPlatform;
    campaigns: Record<
      string,
      {
        campaignName: string;
        adGroups: Record<
          string,
          {
            adGroupName: string;
            keywords: string[];
          }
        >;
      }
    >;
  }
>;

export interface KeywordsData {
  'Account ID': string;
  'Account Name': string;
  'Ad Group ID': string;
  'Ad Group Name': string;
  'Campaign ID': string;
  'Campaign Name': string;
  Platform: string;
  keywords: string[];
}

// Display labels for the ad platform a row originates from.
export const PLATFORM_LABELS: Record<AdPlatform, string> = {
  google_ads: 'Google Ads',
  bing: 'Bing'
};

export const isAdPlatform = (value: string): value is AdPlatform =>
  Object.hasOwn(PLATFORM_LABELS, value);

export const getPlatformLabel = (platform: string): string =>
  isAdPlatform(platform) ? PLATFORM_LABELS[platform] : platform;

// Short, underscore-free token used to prefix the encoded training-row id, so Google and Bing rows that
// happen to share account/campaign/ad-group ids don't collide. (Underscore-free because the id is split
// on '_'.) Keep these stable — they're persisted in InstanceRow.adsUsed.
export const PLATFORM_ID_TOKEN: Record<AdPlatform, string> = {
  google_ads: 'google',
  bing: 'bing'
};

export const NUM_DESCRIPTIONS = 4;
export const NUM_HEADLINES = 15;

export const LANGUAGE_OPTIONS_MAP: Record<string, string> = {
  english: 'English',
  spanish: 'Spanish',
  french: 'French',
  german: 'German',
  italian: 'Italian',
  portuguese: 'Portuguese'
};

export const S3_CDN = 's3://bpm-cdn/';

export const PAYLOAD_LIMIT = 246581; // in bytes

export const getPayloadSize = (payloadString: string): number => {
  const encoder = new TextEncoder();
  return encoder.encode(payloadString).length;
};

export const getCopyTigerIDs = async (
  company: string,
  startDate?: string,
  endDate?: string
): Promise<GoogleAdsIDs[]> => {
  const response = await CopyTigerLambdaFetch('/getCopyTigerIDs', {
    method: 'GET',
    params: { company, startDate, endDate }
  });
  return awaitJSON(response);
};

// Shared Comet-loading fetch for CopyTiger row datasets. `getCopyTigerTrainingOptions` (Train tab,
// performance-filtered) and `getCopyTigerGenerationTargets` (Generate tab, surfaces every live ad group
// regardless of clicks/impressions — DSH-29971) return the same TableRow shape, so they differ only in
// which endpoint they invoke.
// Both lambdas are self-invoke routes (bpm-lambdas' cometInputWrapper/cometOutputWrapper), so this
// calls their own GET route directly via fetchCometSelfInvoke. Stays GET (not POST) since both
// endpoints are semantically reads.
const fetchCopyTigerRows = async (
  path: string,
  company: string,
  startDate: string,
  endDate: string,
  accountID?: number,
  campaignID?: number,
  adGroupID?: number
): Promise<TableRow[]> => {
  const data = await fetchCometSelfInvoke<
    {
      company: string;
      endDate: string;
      startDate: string;
      accountID?: number;
      campaignID?: number;
      adGroupID?: number;
    },
    TrainingRow[]
  >(
    CopyTigerLambdaFetch,
    path,
    { company, endDate, startDate, accountID, campaignID, adGroupID },
    { method: 'GET', noTimeout: true, period: 3000 }
  );
  return data.map(row => {
    const { descriptions, headlines, ...rest } = row;
    const tableRow: Partial<TableRow> = {
      ...rest,
      accountID: parseInt(`${row.accountID}`, 10),
      campaignID: parseInt(`${row.campaignID}`, 10),
      adGroupID: parseInt(`${row.adGroupID}`, 10),
      id: `${PLATFORM_ID_TOKEN[row.platform]}_${row.accountID}_${row.campaignID}_${row.adGroupID}_${row.keywords
        .sort()
        .join(',')}`
    };
    for (let i = 0; i < NUM_DESCRIPTIONS; i++) {
      if (descriptions && descriptions[i]) {
        tableRow[`description${i + 1}`] = descriptions[i];
      }
    }
    for (let j = 0; j < headlines.length; j++) {
      if (headlines[j]) {
        tableRow[`headline${j + 1}`] = headlines[j];
      }
    }
    return tableRow as TableRow;
  });
};

export const getCopyTigerTrainingOptions = (
  company: string,
  startDate: string,
  endDate: string,
  accountID?: number,
  campaignID?: number,
  adGroupID?: number
): Promise<TableRow[]> =>
  fetchCopyTigerRows(
    '/getCopyTigerTrainingOptions',
    company,
    startDate,
    endDate,
    accountID,
    campaignID,
    adGroupID
  );

export const getCopyTigerGenerationTargets = (
  company: string,
  startDate: string,
  endDate: string,
  accountID?: number,
  campaignID?: number,
  adGroupID?: number
): Promise<TableRow[]> =>
  fetchCopyTigerRows(
    '/getCopyTigerGenerationTargets',
    company,
    startDate,
    endDate,
    accountID,
    campaignID,
    adGroupID
  );

export const getCopyTigerStyleGuides = async (company: string): Promise<StyleGuideRow[]> => {
  try {
    const response = await CopyTigerLambdaFetch('/getCopyTigerStyleGuides', {
      method: 'GET',
      params: {
        company
      }
    });
    return await awaitJSON(response);
  } catch (error) {
    throw new Error(`Unable to fetch style guides for ${company}: ${getSafeErrorMessage(error)}`, {
      cause: error
    });
  }
};

export const addCopyTigerStyleGuide = async (
  company: string,
  name: string,
  styleGuideText: string,
  description?: string,
  id?: string
): Promise<StyleGuideRow> => {
  try {
    const response = await CopyTigerLambdaFetch('/addCopyTigerStyleGuide', {
      method: 'POST',
      body: JSON.stringify({
        company,
        name,
        styleGuideText: styleGuideText ? JSON.stringify({ content: styleGuideText }) : '',
        description,
        ...(id && { id })
      })
    });
    return await awaitJSON(response);
  } catch (error) {
    throw new Error(`Unable to add style guide for ${company}: ${getSafeErrorMessage(error)}`, {
      cause: error
    });
  }
};

export const deleteCopyTigerStyleGuide = async (
  company: string,
  id: string
): Promise<{ message: string }> => {
  try {
    const response = await CopyTigerLambdaFetch('/deleteCopyTigerStyleGuide', {
      method: 'DELETE',
      params: {
        company,
        id
      }
    });
    return await awaitJSON(response);
  } catch (error) {
    throw new Error(
      `Unable to delete style guide ${id} for ${company}: ${getSafeErrorMessage(error)}`,
      {
        cause: error
      }
    );
  }
};

export const generateCopyTigerStyleGuide = async (company: string): Promise<string> => {
  try {
    return await fetchCometSelfInvoke<{ company: string }, string>(
      CopyTigerPyLambdaFetch,
      '/genStyleGuide',
      { company },
      { noTimeout: true, period: 3000 }
    );
  } catch (error) {
    throw new Error(
      `Unable to generate style guide for ${company}: ${getSafeErrorMessage(error)}`,
      { cause: error }
    );
  }
};

export const getStyleGuideContent = async (s3Uri: string): Promise<string> => {
  const s3Key = s3Uri.replace(S3_CDN, '');
  const response = await S3SignedUrlFetch2('bpm-cdn', s3Key, 'application/json');
  assertPresignedOk(response, 'Style guide download failed');
  const s3ContentResponse = JSON.parse(await response.text());
  return s3ContentResponse.content;
};

export const getCopyTigerModelInstances = async (company: string): Promise<InstanceRow[]> => {
  try {
    const response = await CopyTigerLambdaFetch('/getCopyTigerInstances', {
      method: 'GET',
      params: {
        company
      }
    });
    return await awaitJSON(response);
  } catch (error) {
    throw new Error(
      `Unable to fetch model instances for ${company}: ${getSafeErrorMessage(error)}`,
      { cause: error }
    );
  }
};

interface AddCopyTigerInstanceArgs {
  adsUsed?: string[];
  company: string;
  description?: string;
  id?: string;
  language: string;
  name: string;
  vectorstoreData?: string | object;
}

export const addCopyTigerModelInstance = async (
  args: AddCopyTigerInstanceArgs
): Promise<InstanceRow> => {
  try {
    const response = await CopyTigerLambdaFetch('/addCopyTigerInstance', {
      method: 'POST',
      body: JSON.stringify(args)
    });
    return await awaitJSON(response);
  } catch (error) {
    throw new Error(
      `Unable to add model instance for ${args.company}: ${getSafeErrorMessage(error)}`,
      { cause: error }
    );
  }
};

export const deleteCopyTigerModelInstance = async (
  company: string,
  id: string
): Promise<{ message: string }> => {
  try {
    const response = await CopyTigerLambdaFetch('/deleteCopyTigerInstance', {
      method: 'DELETE',
      params: {
        company,
        id
      }
    });
    return await awaitJSON(response);
  } catch (error) {
    throw new Error(
      `Unable to delete model instance ${id} for ${company}: ${getSafeErrorMessage(error)}`,
      {
        cause: error
      }
    );
  }
};

export interface GenerateCopyTigerAdsArgs {
  additionalInstructions?: string;
  company: string;
  email?: string;
  instance_uuid: string;
  keywordsData: KeywordsData[];
  styleGuide_uuid?: string;
  temperature: number;
  top_k: number;
  top_p: number;
}

interface GeneratedAdsResponse {
  error?: string;
  generatedAdsData: Record<string, string>[];
}

export const generateCopyTigerAds = async (
  args: GenerateCopyTigerAdsArgs
): Promise<GeneratedAdsResponse> => {
  try {
    const lambdaArgs = {
      ...args,
      keywords: args.keywordsData.map(data => ({ ...data, keywords: data.keywords.join(',') }))
    };
    const size = getPayloadSize(
      JSON.stringify({ lambdaArgs, lambdaName: 'copytiger-generateAds' })
    );
    if (size > PAYLOAD_LIMIT) {
      throw new Error(
        `Too many ads to generate, hitting AWS payload limit. Please generate less ads. Current Payload Size: ${size} bytes. Payload Limit: ${PAYLOAD_LIMIT} bytes.`
      );
    }
    return await fetchCometSelfInvoke<typeof lambdaArgs, GeneratedAdsResponse>(
      CopyTigerPyLambdaFetch,
      '/generateAds',
      lambdaArgs,
      { noTimeout: true, period: 3000 }
    );
  } catch (error) {
    throw new Error(`Unable to generate ads for ${args.company}: ${getSafeErrorMessage(error)}`, {
      cause: error
    });
  }
};

export const getCopyTigerAuditConfigs = async (company: string): Promise<AuditConfig[]> => {
  try {
    const response = await CopyTigerLambdaFetch('/getCopyTigerAuditConfigs', {
      method: 'GET',
      params: { company }
    });
    return (await awaitJSON<AuditConfigRowWire[]>(response)).map(auditConfigFromWire);
  } catch (error) {
    throw new Error(`Unable to fetch audit configs for ${company}: ${getSafeErrorMessage(error)}`, {
      cause: error
    });
  }
};

// Creates a config, or updates it when `config.configID` is set. Returns the saved row.
export const saveCopyTigerAuditConfig = async (
  company: string,
  config: AuditConfigInput
): Promise<AuditConfig> => {
  try {
    const response = await CopyTigerLambdaFetch('/saveCopyTigerAuditConfig', {
      method: 'POST',
      body: JSON.stringify({ company, ...auditConfigToWireBody(config) })
    });
    return auditConfigFromWire(await awaitJSON<AuditConfigRowWire>(response));
  } catch (error) {
    throw new Error(`Unable to save audit config for ${company}: ${getSafeErrorMessage(error)}`, {
      cause: error
    });
  }
};

export const deleteCopyTigerAuditConfig = async (
  company: string,
  configID: string
): Promise<{ message: string }> => {
  try {
    const response = await CopyTigerLambdaFetch('/deleteCopyTigerAuditConfig', {
      method: 'DELETE',
      params: { company, configID }
    });
    return await awaitJSON(response);
  } catch (error) {
    throw new Error(
      `Unable to delete audit config ${configID} for ${company}: ${getSafeErrorMessage(error)}`,
      { cause: error }
    );
  }
};
